import React from 'react';
import { Link } from 'react-router-dom';
import { Target, Users, Lightbulb, TrendingUp, Award, ArrowRight, Heart } from 'lucide-react';

const About = () => {
  const values = [
    {
      icon: Target,
      title: 'Clarity & Simplicity',
      description: 'We cut through the noise to deliver straightforward solutions. No jargon, no complexity—just clear strategies that work and results you can understand.',
      gradient: 'from-primary-400 to-primary-600'
    },
    {
      icon: Users,
      title: 'Trust & Transparency',
      description: 'Honesty is at the heart of everything we do. We build lasting partnerships through open communication, integrity, and delivering on our promises.',
      gradient: 'from-primary-500 to-primary-700'
    },
    {
      icon: Lightbulb,
      title: 'Innovation & Improvement',
      description: 'We never stand still. By embracing cutting-edge AI technology and continuously refining our approach, we ensure your business stays ahead of the curve.',
      gradient: 'from-primary-400 to-primary-600'
    }
  ];

  const stats = [
    { number: '£100K+', label: 'Generated in Extra Sales' },
    { number: '500+', label: 'Leads Reactivated' },
    { number: '24/7', label: 'AI Support Available' },
    { number: '95%', label: 'Client Satisfaction' }
  ];

  return (
    <div className="pt-16">
      <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden bg-gradient-to-br from-primary-600 via-accent-600 to-primary-700 text-white">
        <div className="absolute inset-0 bg-grid-pattern opacity-10"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <div className="inline-block mb-6 px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full border border-white/30">
            <span className="text-white font-semibold text-sm">About MMI Online</span>
          </div>
          
          <h1 className="text-5xl md:text-7xl font-display font-bold mb-6 leading-tight">
            We're in the Business of
            <br />
            <span className="text-primary-200">Getting Results</span>
          </h1>
          
          <p className="text-xl md:text-2xl text-white mb-8 max-w-3xl mx-auto leading-relaxed font-medium">
            We simply leverage the power of AI to deliver exceptional outcomes for our clients
          </p>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center mb-20">
            <div>
              <h2 className="text-4xl md:text-5xl font-display font-bold mb-6">
                <span className="text-gradient">Our Mission</span>
              </h2>
              <p className="text-xl text-gray-600 mb-6 leading-relaxed">
                Our mission is to pull in multiple <span className="font-bold text-primary-600">£1000s in extra sales</span> for our clients without asking them to do any extra work.
              </p>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                We use our AI services to streamline sales processes, increase productivity and turn time-sucking tasks into profit centers. By automating repetitive work and optimizing customer engagement, we help businesses unlock revenue they didn't know existed.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                Whether it's reactivating old leads, responding to customers 24/7, or managing reviews and abandoned carts, our AI solutions work tirelessly so you don't have to.
              </p>
            </div>
            <div className="relative">
              <div className="absolute top-0 right-0 w-64 h-64 bg-primary-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob"></div>
              <div className="absolute bottom-0 left-0 w-64 h-64 bg-accent-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob animation-delay-2000"></div>
              <div className="relative bg-gradient-to-br from-primary-50 to-accent-50 rounded-2xl p-8 shadow-xl">
                <Award className="w-20 h-20 text-primary-600 mb-6" />
                <h3 className="text-2xl font-display font-bold mb-4 text-gray-900">
                  Results That Reward You
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  Our AI solutions deliver measurable rewards for your business—more sales, more time, and more growth without the extra effort.
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-20">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl md:text-5xl font-display font-bold text-gradient mb-2">
                  {stat.number}
                </div>
                <div className="text-gray-600 font-medium">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-br from-gray-50 to-primary-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-display font-bold mb-4">
              <span className="text-gradient">Our Core Values</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              The principles that guide everything we do
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <div
                key={index}
                className="group bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 hover-lift"
              >
                <div className={`inline-flex p-4 rounded-xl bg-gradient-to-br ${value.gradient} mb-6 group-hover:scale-110 transition-transform`}>
                  <value.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-display font-bold mb-4 text-gray-900">
                  {value.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MMI Origin Story Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-display font-bold mb-4">
              <span className="text-gradient">What Does MMI Stand For?</span>
            </h2>
          </div>
          
          <div className="bg-gradient-to-br from-gray-50 to-primary-50 rounded-3xl p-8 md:p-12 shadow-xl">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <div className="inline-flex p-4 rounded-xl bg-gradient-to-br from-pink-500 to-rose-500 mb-6">
                  <Heart className="w-10 h-10 text-white" />
                </div>
                <h3 className="text-3xl font-display font-bold mb-4 text-gray-900">
                  Me, Myself and I
                </h3>
                <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                  The name MMI stands for <span className="font-bold text-primary-600">"Me, Myself and I"</span> — a concept that Alfie came up with when founding the company. It represents the entrepreneurial spirit and the journey that every business owner takes.
                </p>
                <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                  A few weeks after forming the company, Leo joined as a business partner. Both founders loved the name because it perfectly captures the essence of what they do — helping business owners who are on their own journey.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed">
                  We want to relate to other business owners who understand what it's like to build something from the ground up. Whether you're a solo entrepreneur or leading a growing team, we've been there too, and we're here to help you succeed.
                </p>
              </div>
              <div className="relative">
                <div className="absolute top-0 right-0 w-48 h-48 bg-primary-200 rounded-full mix-blend-multiply filter blur-3xl opacity-40 animate-blob"></div>
                <div className="absolute bottom-0 left-0 w-48 h-48 bg-accent-200 rounded-full mix-blend-multiply filter blur-3xl opacity-40 animate-blob animation-delay-2000"></div>
                <div className="relative bg-white rounded-2xl p-8 shadow-lg">
                  <div className="text-center">
                    <div className="text-6xl font-display font-bold text-gradient mb-4">MMI</div>
                    <div className="text-2xl font-semibold text-gray-700 mb-6">Me, Myself and I</div>
                    <div className="space-y-4 text-left">
                      <div className="flex items-center">
                        <div className="w-3 h-3 rounded-full bg-primary-500 mr-3"></div>
                        <span className="text-gray-600">Founded by Alfie</span>
                      </div>
                      <div className="flex items-center">
                        <div className="w-3 h-3 rounded-full bg-accent-500 mr-3"></div>
                        <span className="text-gray-600">Leo joined as partner</span>
                      </div>
                      <div className="flex items-center">
                        <div className="w-3 h-3 rounded-full bg-pink-500 mr-3"></div>
                        <span className="text-gray-600">Built for entrepreneurs</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-primary-600 to-accent-600 rounded-3xl p-12 md:p-16 text-center text-white shadow-2xl">
            <TrendingUp className="w-16 h-16 mx-auto mb-6" />
            <h2 className="text-4xl md:text-5xl font-display font-bold mb-6">
              Why Choose MMI Online?
            </h2>
            <p className="text-xl text-white mb-8 max-w-3xl mx-auto leading-relaxed font-medium">
              We don't just implement AI solutions—we become your partner in growth. Our team combines deep technical expertise with a genuine understanding of business challenges to deliver solutions that truly move the needle.
            </p>
            <p className="text-lg text-white mb-10 max-w-2xl mx-auto font-medium">
              From database reactivation to 24/7 customer engagement, we've helped businesses unlock millions in additional revenue. Let us show you what's possible.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center bg-white text-primary-600 px-8 py-4 rounded-full font-semibold text-lg hover:shadow-2xl hover:scale-105 transition-all duration-300"
            >
              Start Your Journey
              <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
